import { z } from 'zod';
import { newId } from '../ids.js';
import { highlightTypeRowSchema, instantSchema, uuidSchema } from '../schema/rows.js';
import { blockKindSchema, stageCategorySchema } from '../template/manifest.js';
import { DomainError } from './types.js';
export const highlightTypeInput = highlightTypeRowSchema.pick({
    id: true,
    max_per_period: true,
    limit_mode: true,
    eligible_kind: true,
    eligible_stage_category: true,
});
export const activeHighlightInput = z.object({
    id: uuidSchema,
    block_id: uuidSchema,
    rank: z.number().int(),
});
const setHighlightInput = z.object({
    user_id: uuidSchema,
    type: highlightTypeInput,
    period_id: uuidSchema,
    /** The block as it will be once the accompanying change (e.g. a triage decision) applies. */
    block: z.object({
        id: uuidSchema,
        kind: blockKindSchema,
        stage_category: stageCategorySchema.nullable(),
    }),
    /** Highlights of this type in the period that are not removed (achieved ones included). */
    current: z.array(activeHighlightInput),
    mode: z.enum(['add', 'replace', 'remove']),
    /** «Añadir igualmente»: exceed the limit in `warn` mode. Never allowed in `block` mode. */
    force: z.boolean().default(false),
    now: instantSchema.optional(),
});
/** Whether one more highlight would exceed the type's limit (TAI-03: achieved ones count). */
export function highlightLimitReached(type, activeCount) {
    return type.max_per_period !== null && activeCount >= type.max_per_period;
}
/** Whether a block can carry a highlight of this type (INV-07). */
export function isHighlightEligible(type, block) {
    return block.kind === type.eligible_kind && block.stage_category === type.eligible_stage_category;
}
const removal = (id, removedAt) => ({
    table: 'highlights',
    op: 'PATCH',
    id: id,
    data: { removed_at: removedAt },
});
export function setHighlight(input) {
    const parsed = setHighlightInput.parse(input);
    const { type, block, current, mode } = parsed;
    const now = parsed.now ?? new Date().toISOString();
    const own = current.filter((highlight) => highlight.block_id === block.id);
    if (mode === 'remove') {
        return {
            operations: own.map((highlight) => removal(highlight.id, now)),
            undo: own.map((highlight) => removal(highlight.id, null)),
        };
    }
    if (!isHighlightEligible(type, block)) {
        throw new DomainError('invariant_violation', 'Solo las tareas en ASAP pueden ser TAI.', 'INV-07');
    }
    // Already highlighted: nothing to add, nothing to replace.
    if (own.length > 0)
        return { operations: [], undo: [] };
    const replaced = mode === 'replace' ? current : [];
    const remaining = current.length - replaced.length;
    if (highlightLimitReached(type, remaining)) {
        const allowed = type.limit_mode === 'warn' && parsed.force;
        if (!allowed) {
            throw new DomainError('highlight_limit', `Ya tienes ${remaining} de ${type.max_per_period} en este periodo.`, 'INV-08');
        }
    }
    const id = newId();
    const rank = Math.max(0, ...current.map((highlight) => highlight.rank)) + 1;
    const created = {
        table: 'highlights',
        op: 'PUT',
        id,
        data: {
            user_id: parsed.user_id,
            highlight_type_id: type.id,
            block_id: block.id,
            period_id: parsed.period_id,
            rank,
            achieved_at: null,
            removed_at: null,
            created_at: now,
        },
    };
    return {
        // Remove before adding: the server counts the period's highlights in `block` mode.
        operations: [...replaced.map((highlight) => removal(highlight.id, now)), created],
        // Undo removes the new one first, so bringing the replaced ones back never exceeds the limit.
        undo: [removal(id, now), ...replaced.map((highlight) => removal(highlight.id, null))],
    };
}
//# sourceMappingURL=highlight.js.map